import { forwardRef, type InputHTMLAttributes } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/utils';

export interface SJCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
  indeterminate?: boolean;
}

export const SJCheckbox = forwardRef<HTMLInputElement, SJCheckboxProps>(
  ({ className, label, description, indeterminate, checked, disabled, id, ...props }, ref) => {
    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex items-start gap-3 cursor-pointer',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <div className="relative flex items-center justify-center pt-0.5">
          <input
            ref={ref}
            type="checkbox"
            id={id}
            checked={checked}
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-[6px] border-2 border-[var(--sj-border)] bg-[var(--sj-surface)] transition-all',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',
              (checked || indeterminate) && 'border-primary-600 bg-primary-600'
            )}
          >
            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
            {indeterminate && !checked && <Minus className="h-3 w-3 text-white" strokeWidth={3} />}
          </div>
        </div>
        {(label || description) && (
          <div className="flex flex-col">
            {label && <span className="text-sm font-medium text-[var(--sj-text-primary)]">{label}</span>}
            {description && <span className="text-xs text-[var(--sj-text-muted)]">{description}</span>}
          </div>
        )}
      </label>
    );
  }
);

SJCheckbox.displayName = 'SJCheckbox';
